/**
 * The specimen sheet — everything products-api records about one ring or one stone, laid out as the
 * grading report the assay-office sheet has always been pretending to be.
 *
 * Two rules keep it honest:
 * - Nothing unrecorded is rendered. products-api stores "unknown" as a value (`UNSPECIFIED`, `""`,
 *   or an empty array), so a naive sheet states non-facts: "Clarity: UNSPECIFIED". Every row goes
 *   through `isRecorded`, and a section with no recorded rows disappears rather than showing dashes.
 * - Sections follow the source. An INTERNAL stone has no certification columns at all in
 *   products-api, so it gets no certification section — an empty one would read as "uncertified"
 *   when the truth is "never recorded here".
 */
import { Fragment, type ReactNode } from "react";
import { enumLabel, isRecorded, metalDisplay } from "./enums";
import { colourBranch, formatSize, type GemstoneSummary, type RingDetail, type Stone } from "./mapping";

const cx = (...c: Array<string | false | null | undefined>): string => c.filter(Boolean).join(" ");

type Row = [string, ReactNode] | null;

const money = (n: number): string => `£${n.toLocaleString("en-GB")}`;

/** Render only the rows that carry a recorded fact; render nothing at all if none do. */
function Facts({ rows }: { rows: Row[] }) {
  const live = rows.filter((r): r is [string, ReactNode] => r !== null);
  if (live.length === 0) return null;
  return (
    <dl className="sheet-facts">
      {live.map(([k, v]) => (
        <Fragment key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

/**
 * A titled section. Emptiness has to be decided from the rows, not from `children`: a `<Facts/>`
 * element is truthy even when it renders null, so a `!children` guard never fires and the heading
 * strands itself above nothing.
 */
function FactSection({ title, rows }: { title: string; rows: Row[] }) {
  if (rows.every((r) => r === null)) return null;
  return (
    <>
      <div className="lab-divlabel">{title}</div>
      <Facts rows={rows} />
    </>
  );
}

/** A titled section wrapping arbitrary content. Callers must guard their own emptiness. */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <>
      <div className="lab-divlabel">{title}</div>
      {children}
    </>
  );
}

function Photo({ url, alt, all, onPick }: { url: string | null; alt: string; all: string[]; onPick?: (u: string) => void }) {
  return (
    <>
      <div className="sheet-frame">
        {url ? <img src={url} alt={alt} /> : <span className="no-image">no photo</span>}
        <span className="tick tl" />
        <span className="tick tr" />
        <span className="tick bl" />
        <span className="tick br" />
      </div>
      {all.length > 1 && (
        <div className="lab-film sheet-film">
          {all.map((u) => (
            <button key={u} type="button" className={cx(u === url && "selected")} onClick={() => onPick?.(u)} aria-label="Show this photo">
              <img src={u} alt="" />
            </button>
          ))}
        </div>
      )}
    </>
  );
}

/** The rows the trade actually grades a stone on, plus the measurements that back them.
 *  `title` lets a ring's single centre stone own this block outright rather than being summarised
 *  above it and then repeated here. */
function StoneGrading({ stone, title = "grading" }: { stone: Stone; title?: string }) {
  const colour = colourBranch(stone);
  const size = formatSize(stone.size);
  return (
    <>
      <FactSection
        title={title}
        rows={[
          isRecorded(stone.type) ? ["Type", <span className="mono">{stone.type}</span>] : null,
          typeof stone.carat === "number" ? ["Carat", `${stone.carat} ct`] : null,
          isRecorded(stone.shape) ? ["Cut", <span className="mono">{stone.shape}</span>] : null,
          colour ? [colour.label, colour.value] : null,
          isRecorded(stone.clarity) ? ["Clarity", <span className="mono">{stone.clarity}</span>] : null,
          typeof stone.quantity === "number" && stone.quantity !== 1 ? ["Quantity", stone.quantity] : null,
        ]}
      />
      {size && <FactSection title="measurements" rows={[["Dimensions", <span className="mono">{size}</span>]]} />}
    </>
  );
}

/** Certification and provenance. Only Nivoda stones carry lab/number/report — products-api has no
 *  columns for them on own stock — so the section is skipped rather than shown empty. */
function StoneOrigin({ stone }: { stone: Stone }) {
  const hasCert = isRecorded(stone.certificationLab) || isRecorded(stone.certificationNumber) || isRecorded(stone.certificateUrl);
  return (
    <>
      {hasCert && (
        <FactSection
          title="certification"
          rows={[
            isRecorded(stone.certificationLab) ? ["Lab", <span className="mono">{stone.certificationLab}</span>] : null,
            isRecorded(stone.certificationNumber) ? ["Number", <span className="mono">{stone.certificationNumber}</span>] : null,
            isRecorded(stone.certificateUrl)
              ? [
                  "Report",
                  <a className="sheet-link" href={stone.certificateUrl} target="_blank" rel="noreferrer">
                    View ↗
                  </a>,
                ]
              : null,
          ]}
        />
      )}
      <FactSection
        title="provenance"
        rows={[
          typeof stone.labGrown === "boolean" ? ["Origin", stone.labGrown ? "Lab-grown" : "Natural"] : null,
          typeof stone.certified === "boolean" && !hasCert ? ["Certified", stone.certified ? "Yes" : "No"] : null,
          isRecorded(stone.source) ? ["Source", <span className="mono">{stone.source}</span>] : null,
          isRecorded(stone.supplier) ? ["Supplier", stone.supplier] : null,
          isRecorded(stone.supplierReference) ? ["Reference", <span className="mono sheet-ref">{stone.supplierReference}</span>] : null,
        ]}
      />
    </>
  );
}

export function StoneSheet({ gem, photo, onPhoto }: { gem: GemstoneSummary; photo: string | null; onPhoto: (u: string) => void }) {
  return (
    <div className="sheet">
      <Photo url={photo} alt={gem.name} all={gem.imageUrls} onPick={onPhoto} />
      <div className="sheet-id">
        <span className="sku">{gem.sku ?? gem.id}</span>
        <h3>{gem.name}</h3>
      </div>
      <StoneGrading stone={gem.stone} />
      <StoneOrigin stone={gem.stone} />
      <FactSection
        title="commercial"
        rows={[
          typeof gem.price === "number" ? ["Price", money(gem.price)] : null,
          typeof gem.stockRemaining === "number" ? ["Stock", gem.stockRemaining] : null,
        ]}
      />
      <FactSection title="the swap" rows={[["stone_sku", <span className="mono sheet-ref">{gem.id}</span>]]} />
    </div>
  );
}

/** One centre stone, condensed. The baseline marker matters: the bench infers the operation from a
 *  single baseline stone, and on a multi-stone ring that is simply the first one. */
function CentreStone({ stone, baseline }: { stone: Stone; baseline: boolean }) {
  const colour = colourBranch(stone);
  const bits = [
    isRecorded(stone.shape) ? stone.shape : null,
    typeof stone.carat === "number" ? `${stone.carat}ct` : null,
    isRecorded(stone.clarity) ? stone.clarity : null,
    colour?.value,
  ].filter((b): b is string => !!b);
  return (
    <li className="sheet-stone">
      <span className="t">{stone.type ?? "stone"}</span>
      <span className="b">{bits.join(" · ")}</span>
      {baseline && <span className="mark">baseline</span>}
    </li>
  );
}

export function RingSheet({ ring, photo, onPhoto }: { ring: RingDetail; photo: string | null; onPhoto: (u: string) => void }) {
  const urls = ring.images.map((i) => i.url);
  return (
    <div className="sheet">
      <Photo url={photo} alt={ring.sku} all={urls} onPick={onPhoto} />
      <div className="sheet-id">
        <span className="sku">{ring.sku}</span>
        <h3>{ring.name}</h3>
        {ring.productType && <span className="mono small muted">{ring.productType}</span>}
      </div>

      {ring.warnings.map((w) => (
        <p key={w} className="lab-warnline">
          ⚠ {w}
        </p>
      ))}

      {ring.metals.length > 0 && (
        <Section title={ring.metals.length === 1 ? "metal" : "metals offered"}>
          <ul className="sheet-metals">
            {ring.metals.map((m) => {
              const d = metalDisplay(m.metal);
              return (
                <li key={m.metal} className={cx(m.metal === ring.defaultMetal && "is-default")}>
                  <span className={cx("bead", d.swatchClass)} />
                  <span className="nm">
                    {d.family} {d.karat}
                  </span>
                  {m.metal === ring.defaultMetal && <span className="mark">{m.isDefault ? "default" : "assumed"}</span>}
                  {typeof m.castingWeight === "number" && <span className="wt">{m.castingWeight}g</span>}
                </li>
              );
            })}
          </ul>
        </Section>
      )}

      {/* One centre stone owns the grading block outright. More than one, and grading each in full
          would bury the sheet, so they condense to a line apiece with the baseline marked. */}
      {ring.centreStones.length === 1 && <StoneGrading stone={ring.centreStones[0]!} title="centre stone" />}
      {ring.centreStones.length > 1 && (
        <Section title={`centre stones · ${ring.centreStones.length}`}>
          <ul className="sheet-stones">
            {ring.centreStones.map((s, i) => (
              <CentreStone key={`${s.type}-${i}`} stone={s} baseline={i === 0} />
            ))}
          </ul>
        </Section>
      )}

      {ring.accents && (
        <FactSection
          title="accents"
          rows={[
            ring.accents.totalCarats > 0 ? ["Total", `${ring.accents.totalCarats} ct`] : null,
            ring.accents.colouredAccents ? ["Coloured", "Yes"] : null,
            ring.accents.modelCode ? ["Model", <span className="sheet-model">{ring.accents.modelCode}</span>] : null,
          ]}
        />
      )}

      <FactSection
        title="commercial"
        rows={[
          typeof ring.price === "number" ? ["Price", money(ring.price)] : null,
          typeof ring.settingPrice === "number" && ring.settingPrice > 0 ? ["Setting only", money(ring.settingPrice)] : null,
          typeof ring.stockRemaining === "number" ? ["Stock", ring.stockRemaining] : null,
          ring.size ? ["Size", <span className="mono">{ring.size}</span>] : null,
          ring.audience ? ["Audience", enumLabel(ring.audience)] : null,
        ]}
      />

      <FactSection
        title="photos"
        rows={[
          ["Available", `${ring.images.length}`],
          ring.images.length > 1 ? ["all_images", <span className="mono">fans out to {ring.images.length} jobs</span>] : null,
        ]}
      />
    </div>
  );
}
